import { useRef, useState, useEffect } from 'react';

import parse from 'html-react-parser';
import styled, { css } from 'styled-components';
import { get } from 'lodash';
import { Show } from '../../show';

import GridContainer from '../../layouts/components/grid-container';
import FluidContainer from '../../layouts/components/fluid-container';

import { Question, PublicTitle } from '../components';
import ActionChip from '../../action-chip';
import AudioPlayer from '../../audio-player';
import Gap from '../../gap';
import Geogebra from '../../geogebra';
import ImageGallery from '../../image-gallery';
import SharedText from '../../shared-text';
import YouTubePlayer from '../../video-player/youtube';
import { exerciseDefaultProps, exercisePropTypes } from '../../../constants/exercise';
import { parseElement } from '../../../lib/exercise';
import { useWindowSize } from '../../../lib/hooks';

import HintBox from '../../hint-box';
import { getDeviceType, parseMfenced } from '../../../lib/utils';
import ChoiceChip from '../../choice-chip';
import Stack from '../../stack';
import MathContent from '../../math-content';

const Wrapper = styled.span`
    display: inline-flex;
    margin: 2px;
`;

const ActionChipWrapper = styled.div`
    margin: 8px;
`;

export const Icon = styled.img`
    height: 18px;
    width: 18px;

    :hover {
        filter: brightness(0.5);
    }
`;

export const FluidBlink = styled(FluidContainer)`
    margin-top: 24px;

    ${(props) =>
        props.toBlink &&
        css`
            animation: blink 1s cubic-bezier(0.6, 0.25, 0.5, 0.8) 2;
        `};
`;

const DragDrop = ({
    answers = [],
    exercise,
    onAnswer,
    correctionStatus,
    onPlayVideo,
    videos,
    show,
    hasAttachments = exerciseDefaultProps.hasAttachments,
    hintBoxIsOpen,
    toggleHintBox,
}) => {
    const { innerWidth } = useWindowSize();
    let question = exercise.question.replace(/<gap_ref (.*?)\/>/g, '<gap_ref $1></gap_ref>');

    const [gapsMapping, setGapsMapping] = useState(new Map());
    const [selectedGapId, setSelectedGapId] = useState(null);
    const [selectedChipId, setSelectedChipId] = useState(null);
    const [selectedInsertedChip, setSelectedInsertedChip] = useState(null);

    const [isBlink, setBlink] = useState(false);

    const myRef = useRef();
    const gapRef = useRef();
    const scrollToRef = (ref) => window.scrollTo({ behavior: 'smooth', top: ref.current.offsetTop - 92 });

    useEffect(() => {
        setGapsMapping(parseElement(question, exercise.options));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setTimeout(() => {
            setBlink(false);
        }, 2200);
    }, [isBlink]);

    /**
     * Handles when click on inserted chip
     * @param {*} gapId filled and optionId selected
     */
    const onSelectInsertedChip = ({ gapId, optionId }) => {
        // Click on available option, then on an inserted chip [swap them]
        if (selectedChipId !== null) {
            const filteredAnswers = answers.filter((item) => item.gapId !== gapId);
            onAnswer(exercise.id, [...filteredAnswers, { gapId: gapId, optionId: selectedChipId }]);
            resetSelection();
            return;
        }

        // If clicked on empty chip
        if (selectedGapId !== null) {
            const filteredAnswers = answers.filter((item) => item.gapId !== gapId);
            onAnswer(exercise.id, [...filteredAnswers, { gapId: selectedGapId, optionId: optionId }]);
            resetSelection();
            return;
        }

        // Click on an inserted chip for the first time
        if (selectedInsertedChip === null) {
            setSelectedInsertedChip({ gapId, optionId });
            return;
        }

        // Click on an inserted chip, after clicked on another one [swap them]
        const filteredOldAnswers = answers.filter((item) => item.gapId !== gapId && item.gapId !== selectedInsertedChip.gapId);

        onAnswer(exercise.id, [
            ...filteredOldAnswers,
            { gapId: gapId, optionId: selectedInsertedChip.optionId },
            { gapId: selectedInsertedChip.gapId, optionId: optionId },
        ]);

        resetSelection();
    };

    /**
     * Handles when click on empty chip
     * @param {*} gapId gap id clicked
     */
    const onSelectEmptyChip = (gapId) => {
        if (selectedChipId !== null) {
            onAnswer(exercise.id, [...answers, { gapId: gapId, optionId: selectedChipId }]);
            resetSelection();
            return;
        }

        if (selectedInsertedChip === null) {
            setSelectedGapId(selectedGapId === gapId ? null : gapId);
            return;
        }

        const filteredAnswers = answers.filter((item) => item.gapId !== selectedInsertedChip.gapId);

        onAnswer(exercise.id, [...filteredAnswers, { gapId: gapId, optionId: selectedInsertedChip.optionId }]);

        resetSelection();
    };

    /**
     * Handles when click on option available
     * @param {*} optionId
     */
    const onSelectOptionChip = (optionId) => {
        // If clicked an inserted chip [swap]
        if (selectedInsertedChip !== null) {
            const filteredAnswers = answers.filter((item) => item.optionId !== selectedInsertedChip.optionId);
            onAnswer(exercise.id, [...filteredAnswers, { gapId: selectedInsertedChip.gapId, optionId: optionId }]);
            resetSelection();
            return;
        }

        // If clicked on a empty chip
        if (selectedGapId !== null) {
            onAnswer(exercise.id, [...answers, { gapId: selectedGapId, optionId }]);
            resetSelection();

            if (innerWidth < 767) {
                window.scrollTo({ behavior: 'smooth', top: gapRef.current.offsetTop - 160 });
            }

            return;
        }

        setSelectedChipId(selectedChipId === optionId ? null : optionId);
    };

    /**
     * Reset all selections.
     */
    const resetSelection = () => {
        setSelectedChipId(null);
        setSelectedGapId(null);
        setSelectedInsertedChip(null);
    };

    const removeInsertedChip = (gapId) => {
        const filteredAnswers = answers.filter((item) => item.gapId !== gapId);
        onAnswer(exercise.id, filteredAnswers);
        resetSelection();
    };

    const options = {
        replace: (domNode) => {
            if (domNode.name && domNode.name === 'mfenced') {
                return parseMfenced(domNode, options);
            }

            if (domNode.name && domNode.name === 'gap_ref') {
                const gapId = domNode.attribs.idref.replace('_', '');

                const responseCurrentGap = answers.find((item) => item.gapId === gapId);
                const response = gapsMapping.get(domNode.attribs.idref) || {};

                if (responseCurrentGap) {
                    const selectedChipValue = get(
                        exercise.options.find((item) => item.id === responseCurrentGap.optionId),
                        'values[0].text',
                    );

                    return (
                        <Wrapper>
                            {correctionStatus ? (
                                <ChoiceChip
                                    id={responseCurrentGap.optionId}
                                    value={selectedChipValue}
                                    type="validated"
                                    handleClick={() => {}}
                                    validationType={selectedChipValue === response.text ? 'success' : 'error'}
                                    badgeIndex={response.char}
                                />
                            ) : (
                                <ActionChip
                                    id={responseCurrentGap.optionId}
                                    value={selectedChipValue}
                                    type="inserted"
                                    handleClick={() => {
                                        onSelectInsertedChip({
                                            gapId: gapId,
                                            optionId: responseCurrentGap.optionId,
                                        });
                                    }}
                                    handleBeginDrag={() => {
                                        resetSelection();
                                        setSelectedInsertedChip({
                                            gapId: gapId,
                                            optionId: responseCurrentGap.optionId,
                                        });
                                    }}
                                    selected={selectedInsertedChip && selectedInsertedChip.optionId === responseCurrentGap.optionId}
                                    validationType={selectedChipValue === response.text ? 'success' : 'error'}
                                    badgeIndex={response.char}
                                    onClose={() => removeInsertedChip(gapId)}
                                />
                            )}
                        </Wrapper>
                    );
                } else {
                    return (
                        <Wrapper>
                            {correctionStatus ? (
                                <ChoiceChip
                                    id={response.responseId}
                                    value={response.text}
                                    type="validated"
                                    validationType="missing"
                                    badgeIndex={response.char}
                                />
                            ) : (
                                <span ref={selectedGapId === gapId ? gapRef : null}>
                                    <Gap idRef={gapId} onClick={() => onSelectEmptyChip(gapId)} selected={selectedGapId === gapId} />
                                </span>
                            )}
                        </Wrapper>
                    );
                }
            }
        },
    };

    let parsedQuestion = parse(question, options);

    const hintBoxLabel =
        getDeviceType() === 'DESKTOP'
            ? `Hai due modi: 1) clicca, tieni premuto su una risposta e trascinala sullo spazio in cui vuoi posizionarla; 2) clicca sulla risposta che vuoi spostare e poi clicca sullo spazio in cui vuoi posizionarla, o viceversa.`
            : `Hai due modi: 1) tocca, tieni premuto su una risposta e trascinala sullo spazio in cui vuoi posizionarla; 2) tocca la risposta che vuoi spostare e poi tocca lo spazio in cui vuoi posizionarla, o viceversa.`;

    return (
        <>
            <Show when={show.title && exercise.publicTitle}>
                <FluidContainer paddingBottom={24}>
                    <PublicTitle>{exercise.publicTitle}</PublicTitle>
                </FluidContainer>
            </Show>

            <Stack direction="vertical" gap="small">
                <Show when={show.prompt && exercise.prompt}>
                    <FluidContainer>
                        <MathContent>{exercise.prompt}</MathContent>
                    </FluidContainer>
                </Show>
                <Show when={show.hintBox}>
                    <HintBox label={hintBoxLabel} isOpen={hintBoxIsOpen} exerciseType={exercise.typeLabel} toggleHintBox={toggleHintBox} />
                </Show>
            </Stack>

            <Show when={show.attachments && hasAttachments.geogebra}>
                <FluidContainer paddingBottom={24}>
                    <Geogebra exerciseId={exercise.id} src={exercise.attachments.geogebra?.src} />
                </FluidContainer>
            </Show>
            <Show when={show.sharedText && hasAttachments.sharedText}>
                <FluidContainer paddingBottom={24} flexWrap="wrap">
                    <SharedText exercise={exercise} />
                </FluidContainer>
            </Show>
            {show.attachments && hasAttachments.audio && (
                <FluidContainer paddingBottom={24}>
                    <AudioPlayer source={exercise.attachments.audio?.src} />
                </FluidContainer>
            )}
            <GridContainer columns={[1, 2]} paddingBottom={24} marginTop={16}>
                <FluidContainer flexDirection={['column-reverse', 'column']}>
                    <div>
                        <FluidBlink
                            style={{ width: innerWidth <= 750 ? '100vw' : '100%' }}
                            ref={myRef}
                            backgroundColor="accent-azure-lighter"
                            maxWidth="initial"
                            marginX={[-16, 0]}
                            marginBottom="tablet"
                            padding="small"
                            toBlink={isBlink}
                            flexWrap="wrap"
                        >
                            {exercise.options.map((option, key) => {
                                const chipUsed = answers.find((item) => item.optionId === option.id);
                                const associatedResponse = gapsMapping.get(`_${option.id}`) || {};
                                const badgeIndex = associatedResponse.char;

                                return (
                                    <ActionChipWrapper key={`${exercise.id}_${key}`}>
                                        <ActionChip
                                            badgeIndex={correctionStatus ? badgeIndex : null}
                                            disabled={!!chipUsed || !!correctionStatus}
                                            id={option.id}
                                            value={option.values[0].text}
                                            handleClick={() => onSelectOptionChip(option.id)}
                                            handleBeginDrag={() => {
                                                resetSelection();
                                                setSelectedChipId(option.id);
                                            }}
                                            selected={selectedChipId === option.id}
                                            type={correctionStatus && badgeIndex ? 'validated' : 'listed'}
                                            validationType="missing"
                                        />
                                    </ActionChipWrapper>
                                );
                            })}
                        </FluidBlink>
                    </div>
                    <Show when={show.attachments}>
                        <Stack gap="desktop">
                            {hasAttachments.video && (
                                <YouTubePlayer
                                    videoIdOrSource={exercise.attachments.video?.src}
                                    onPlayVideo={() => onPlayVideo(exercise.id)}
                                    showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                                />
                            )}
                            {hasAttachments.images && <ImageGallery images={get(exercise, 'attachments.images')} />}
                        </Stack>
                    </Show>
                </FluidContainer>

                <Question>{parsedQuestion}</Question>
            </GridContainer>
        </>
    );
};

DragDrop.propTypes = exercisePropTypes;

export default DragDrop;
